/**
 * hello.js — prove to the discovery server which key this peer holds
 * (peers-prove-who-they-are-to-the-discovery-server, step 1).
 *
 * The server's `welcome` carries a nonce for this socket; `register` carries
 * the proof: a compact JWS, `typ: "acequia-hello"`, signed with this
 * browser's (or process's) user key, its public JWK in the protected header,
 * its RFC 7638 thumbprint as `kid`, over
 *
 *     { nonce, aud: <domain>, group, instanceId, deviceId, userId, iat }
 *
 * The server's half is localDiscovery `src/hello.mjs`; the format is the
 * platform page's "The hello", so a native peer can make one too.
 */
import { jose } from '../deps.js'

export const HELLO_TYP = 'acequia-hello'

/** The JWS algorithm for a WebCrypto key, or null for one we do not sign hellos with. */
export function helloAlg(key) {
    const name = key?.algorithm?.name
    if (name === 'RSA-PSS') return 'PS256'
    if (name === 'ECDSA' && key.algorithm.namedCurve === 'P-256') return 'ES256'
    return null
}

/** Only the members that name a public key: nothing private, nothing extra. */
function publicMembers(jwk) {
    if (jwk.kty === 'RSA') return { kty: 'RSA', n: jwk.n, e: jwk.e }
    if (jwk.kty === 'EC') return { kty: 'EC', crv: jwk.crv, x: jwk.x, y: jwk.y }
    return null
}

/**
 * Sign a hello.
 *
 * @param {object} opts
 * @param {{ privateKey: CryptoKey, publicKey: CryptoKey }} opts.keys - the user key
 * @param {string} opts.nonce - from the server's welcome
 * @param {string} opts.domain - the welcome's `domain`
 * @param {string} opts.group - the welcome's `group`
 * @param {{ instanceId, deviceId, userId }} opts.deviceInfo - what this peer registers with
 * @returns {Promise<string|null>} the compact JWS, or null with no key or no challenge
 */
export async function signHello({ keys, nonce, domain, group, deviceInfo }) {
    if (!keys?.privateKey || !keys?.publicKey || !nonce) return null
    const alg = helloAlg(keys.privateKey)
    if (!alg) return null
    const jwk = publicMembers(await jose.exportJWK(keys.publicKey))
    if (!jwk) return null
    const kid = await jose.calculateJwkThumbprint(jwk)
    const payload = {
        nonce,
        aud: domain,
        group,
        instanceId: deviceInfo?.instanceId ?? null,
        deviceId: deviceInfo?.deviceId ?? null,
        userId: deviceInfo?.userId ?? null,
        iat: Math.floor(Date.now() / 1000),
    }
    return new jose.CompactSign(new TextEncoder().encode(JSON.stringify(payload)))
        .setProtectedHeader({ alg, kid, jwk, typ: HELLO_TYP })
        .sign(keys.privateKey)
}
